/**
 * Configuration — Support Tickets — workflow helpers.
 */

import { getActorDisplayName } from './teamConfig';
import {
  MESSAGES,
  SUPPORT_TICKET_MODULE,
  VALIDATION_MESSAGES,
  VISITOR_ENTRY_POINT,
  collectSupportTicketConfigValidationErrors,
  normalizeSupportTicketConfiguration,
  supportTicketConfigHasChanges,
} from './supportTicketConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';

export { MESSAGES, VALIDATION_MESSAGES };

export function buildSupportTicketConfigActor(auth) {
  if (!auth) return { name: 'Saim Sahi', accountId: '—' };
  return { name: getActorDisplayName(auth) || 'Saim Sahi', accountId: '—' };
}

function stampUpdated(config, actorName, nowIso) {
  return {
    ...config,
    lastUpdated: nowIso,
    lastUpdatedBy: actorName,
  };
}

/** Visitor Entry Point is always the fixed shared value (retained even when Disabled). */
export function getVisitorEntryPointDisplay() {
  return VISITOR_ENTRY_POINT;
}

/**
 * Save availability, entry points, priority and status together to the same Rule.
 * No-change Save returns success without updating metadata.
 */
export function mergeSupportTicketConfigAfterSave(
  allConfigurations,
  draft,
  actor,
  { openedLastUpdated = null } = {},
) {
  const existing = (allConfigurations || []).find((row) => row.id === draft?.id);
  if (!existing) {
    return {
      configurations: allConfigurations,
      success: false,
      message: VALIDATION_MESSAGES.ruleUnavailable,
      errors: { form: VALIDATION_MESSAGES.ruleUnavailable },
      noChange: false,
    };
  }

  const baseline = normalizeSupportTicketConfiguration(existing);
  const nextDraft = normalizeSupportTicketConfiguration({
    ...existing,
    ...draft,
    id: existing.id,
    module: SUPPORT_TICKET_MODULE,
    ticketType: existing.ticketType,
    dateAdded: existing.dateAdded,
    visitorEntryPoint: VISITOR_ENTRY_POINT,
  });

  const errors = collectSupportTicketConfigValidationErrors(nextDraft, {
    baseline,
    allConfigurations,
    openedLastUpdated: openedLastUpdated ?? existing.lastUpdated,
  });

  if (Object.keys(errors).length > 0) {
    return {
      configurations: allConfigurations,
      success: false,
      message: MESSAGES.saveFailed,
      errors,
      noChange: false,
    };
  }

  if (!supportTicketConfigHasChanges(baseline, nextDraft)) {
    return {
      configurations: allConfigurations,
      config: baseline,
      success: true,
      message: MESSAGES.noChange,
      errors: {},
      noChange: true,
    };
  }

  const nowIso = new Date().toISOString();
  const actorName = actor?.name || 'Saim Sahi';
  const updated = stampUpdated(nextDraft, actorName, nowIso);

  try {
    emitRuleSaveHistory({
      isNew: false,
      previous: baseline,
      next: updated,
      module: 'Support Tickets',
      sourceModuleRef: 'support_tickets',
      changedItem: updated.ticketType || updated.ruleName || updated.id,
      itemId: updated.id,
      changeReason: draft?.changeReason,
      actor: actorName,
      stamp: nowIso,
      compareKeys: [
        'ticketType',
        'visitorAvailable',
        'customerAvailable',
        'businessAvailable',
        'customerEntryPoints',
        'businessEntryPoints',
        'automaticPriority',
        'status',
      ],
    });
  } catch {
    // Saved change and required history must complete together.
    return {
      configurations: allConfigurations,
      success: false,
      message: MESSAGES.saveFailed,
      errors: { form: MESSAGES.saveFailed },
      noChange: false,
    };
  }

  return {
    configurations: allConfigurations.map((row) => (row.id === updated.id ? updated : row)),
    config: updated,
    success: true,
    message: MESSAGES.saved,
    errors: {},
    noChange: false,
  };
}
